import { CountryCode, getExampleNumber } from 'libphonenumber-js/mobile';
import examples from 'libphonenumber-js/mobile/examples';

export type PhoneNumberFormat = 'international' | 'national' | 'e164';

export interface PhoneLocaleConfig {
  country: CountryCode;
  format: PhoneNumberFormat;
  placeholder: string;
  priorityCountries?: CountryCode[];
}

function generatePlaceholder(country: CountryCode, format: PhoneNumberFormat): string {
  try {
    const exampleNumber = getExampleNumber(country, examples);
    if (!exampleNumber) {
      return '';
    }

    switch (format) {
      case 'international':
        return exampleNumber.formatInternational();
      case 'national':
        return exampleNumber.formatNational();
      case 'e164':
        return exampleNumber.format('E.164');
      default:
        return exampleNumber.formatNational();
    }
  } catch (error) {
    console.warn(`Failed to generate placeholder for ${country}:`, error);
    return '';
  }
}

function createLocaleConfig(
  country: CountryCode,
  format: PhoneNumberFormat = 'national',
  priorityCountries?: CountryCode[]
): PhoneLocaleConfig {
  return {
    country,
    format,
    placeholder: generatePlaceholder(country, format),
    priorityCountries,
  };
}

export const LOCALE_PHONE_CONFIG: Record<string, PhoneLocaleConfig> = {
  'en-US': createLocaleConfig('US', 'national', ['US', 'CA', 'MX', 'GB']),
  'en-GB': createLocaleConfig('GB', 'national', ['GB', 'IE', 'US']),
  'es-MX': createLocaleConfig('MX', 'national', ['MX', 'US', 'ES']),
  'fr-FR': createLocaleConfig('FR', 'national', ['FR', 'BE', 'CH', 'CA']),
};

export const DEFAULT_PHONE_LOCALE_CONFIG: PhoneLocaleConfig = createLocaleConfig('US', 'national', ['US', 'GB', 'CA', 'MX']);

export function getLocaleConfigForLanguage(languageCode: string): PhoneLocaleConfig {
  const normalizedCode = languageCode.toLowerCase();
  const exactMatch = Object.keys(LOCALE_PHONE_CONFIG).find((key) => key.toLowerCase() === normalizedCode);
  if (exactMatch) return LOCALE_PHONE_CONFIG[exactMatch];
  const baseLanguage = languageCode.split('-')[0].toLowerCase();
  const matchingKey = Object.keys(LOCALE_PHONE_CONFIG).find((key) => key.toLowerCase().startsWith(`${baseLanguage}-`));
  return matchingKey ? LOCALE_PHONE_CONFIG[matchingKey] : DEFAULT_PHONE_LOCALE_CONFIG;
}

